import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  DfIconRegistry,
  DfIconChevronDown16,
  DfIconChevronUp16,
} from '../../../../icon';
import { DfButtonSplitMenuItemComponent } from './button-split-menu-item.component';

/**
 * Split Button Component
 * This component is used to display a primary action button next to another button that acts
 * as a drop down list for other actions.
 *
 * Uses ng-bootstrap Dropdown `https://ng-bootstrap.github.io/#/components/dropdown/examples`
 *
 * @param buttonClass (Optional) Default value is `df-button-passive`.
 * @param displayText Primary button's display text.
 * @param dropdownPosition (Optional) If set to `bottom-right`, dropdown menu will be right-aligned instead of left.
 * @param icon Icon the be added in to the primary button to the left of the Title.
 * @param primaryAction Event emitter to pass the action to be taken back to the parent component.
 *
 * EXAMPLE USAGE:
 * @example
 * ```
 * <df-split-button
 *   [displayText]="displayText"
 *   [buttonClass]="df-button-primary"
 *   [dropdownPosition]="bottom-right"
 *   [icon]="plus"
 *   (primaryAction)="showModal($event)"
 * >
 *  <df-split-button-menu-item
 *   [displayText]="Delete"
 *   [icon]="delete"
 *   (buttonAction)="showModalDelete($event)">
 *  </df-button-split-menu-item>
 *
 *  <df-split-button-menu-item
 *   [displayText]="Add"
 *   [icon]="plus"
 *   (buttonAction)="showModalAdd($event)">
 * </df-button-split-menu-item>
 *
 * </df-button-split>
 * ```
 */

@Component({
  selector: 'df-button-split',
  templateUrl: './button-split.component.html',
  styleUrls: ['./button-split.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfButtonSplitComponent {
  @ContentChildren(DfButtonSplitMenuItemComponent)
  public menuItem: DfButtonSplitMenuItemComponent[];

  @Input() public buttonClass = 'df-button-passive';
  @Input() public displayText: string;
  @Input() public dropdownPosition = 'bottom-right';
  @Input() public icon: string;
  @Output() public primaryAction: EventEmitter<any> = new EventEmitter();

  constructor(private IconRegistry: DfIconRegistry) {
    this.IconRegistry.registerIcons([DfIconChevronDown16, DfIconChevronUp16]);
  }

  /**
   * Emit click event to parent
   */
  public buttonAction($event) {
    this.primaryAction.emit($event);
  }
}
